import { useRef } from "react";

/**
 * Renders a video that shows a real first-frame poster instead of a blank tile
 * with only a play button — without needing a server-generated poster image or
 * bucket CORS (which canvas capture would require).
 *
 * - The `#t=<seekTo>` media fragment is placed on the <video> `src` itself
 *   (browsers honor it there far more reliably than on a <source> child) so the
 *   browser renders the frame at that time as the still.
 * - `preload="metadata"` keeps it lightweight (no full download).
 * - On `loadedmetadata` we nudge `currentTime` to force the browser to decode
 *   and paint that frame as a fallback for browsers that ignore `#t`.
 */
function VideoThumb({ src, seekTo = 0.5, ...rest }) {
  const ref = useRef(null);
  if (!src) return null;

  return (
    <video
      {...rest}
      ref={ref}
      src={`${src}#t=${seekTo}`}
      controls
      playsInline
      preload="metadata"
      onLoadedMetadata={(e) => {
        try {
          if (!e.currentTarget.currentTime) e.currentTarget.currentTime = seekTo;
        } catch {
          /* seeking not supported — ignore */
        }
      }}
    >
      Your browser does not support the video tag.
    </video>
  );
}

export default VideoThumb;
